import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { productionLogger } from '@/utils/production-logger';
import { 
  fetchInitialProducts,
  fetchAllProducts, 
  fetchAllCollections, 
  fetchProductByHandle as apiFetchProductByHandle,
  fetchCollectionProducts,
  fetchCollectionPage,
  createCartPermalink as apiCreateCartPermalink,
  type ShopifyProduct,
  type ShopifyCollection
} from '@/utils/shopifyApi';

/**
 * React Query-based Shopify hook
 * 
 * Replaces useShopify with React Query for better caching, background updates, and state management.
 * Removes two-phase loading and localStorage caching in favor of React Query's built-in mechanisms.
 */

const QUERY_KEYS = {
  products: ['shopify', 'products'] as const,
  productsInitial: ['shopify', 'products', 'initial'] as const,
  collections: ['shopify', 'collections'] as const,
  productByHandle: (handle: string) => ['shopify', 'product', handle] as const,
  collectionProducts: (handle: string) => ['shopify', 'collection', handle] as const,
  collectionPage: (handle: string, page: number) => ['shopify', 'collection', handle, 'page', page] as const,
};

export const useShopifyQuery = () => {
  const queryClient = useQueryClient();

  // Fetch initial 24 products for instant display
  const {
    data: initialProducts = [],
    isLoading: initialLoading,
  } = useQuery({
    queryKey: QUERY_KEYS.productsInitial,
    queryFn: async () => {
      try {
        return await fetchInitialProducts();
      } catch (error: any) {
        productionLogger.error('Error fetching initial products:', error);
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Fetch all products in background (only starts after initial products load)
  const {
    data: allProducts = [],
    isLoading: allProductsLoading,
    error: productsError,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: QUERY_KEYS.products,
    queryFn: async () => {
      try {
        return await fetchAllProducts();
      } catch (error: any) {
        productionLogger.error('Error fetching all Shopify products:', error);
        toast.error('Some products unavailable', {
          description: 'Showing available products',
        });
        return initialProducts; // Fallback to initial products
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: initialProducts.length > 0, // Only fetch after initial load
  });

  // Use all products if available, otherwise use initial products
  const products = allProducts.length > 0 ? allProducts : initialProducts;
  const productsLoading = initialLoading;

  // Fetch all collections with React Query
  const {
    data: collections = [],
    isLoading: collectionsLoading,
    error: collectionsError,
    refetch: refetchCollections,
  } = useQuery({
    queryKey: QUERY_KEYS.collections,
    queryFn: async () => {
      try {
        return await fetchAllCollections();
      } catch (error: any) {
        productionLogger.error('Error fetching Shopify collections:', error);
        toast.error('Shopify collections unavailable', {
          description: `${error?.message || 'Network error'} — retried up to 3x`,
        });
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - collections rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in memory longer
    refetchOnWindowFocus: false,
    retry: 1, // Faster failure for better UX
  });

  const loading = productsLoading || collectionsLoading;

  /**
   * Get a single product by handle
   * Uses React Query's queryClient to check cache first, then fetches if needed
   */
  const getProductByHandle = async (handle: string): Promise<ShopifyProduct | null> => {
    try {
      // Check if we already have this product in the products cache
      const cachedProduct = products.find(p => p.handle === handle);
      if (cachedProduct) {
        return cachedProduct;
      }

      // Otherwise fetch it directly
      const product = await apiFetchProductByHandle(handle);
      if (!product) {
        toast.error("Error", {
          description: "Product not found",
        });
        return null;
      }
      return product;
    } catch (error: any) {
      productionLogger.error('Error fetching product by handle:', error);
      toast.error("Error", {
        description: "Product not found",
      });
      return null;
    }
  };

  /**
   * Get products by collection handle
   * First checks if collection exists, then filters products by collection's product handles
   */
  const getProductsByCollection = async (collectionHandle: string): Promise<ShopifyProduct[]> => {
    try {
      const targetCollection = collections.find(c => c.handle === collectionHandle);
      
      if (!targetCollection || !targetCollection.productHandles || targetCollection.productHandles.length === 0) {
        return [];
      }

      // Filter products by collection's product handles
      const collectionProducts = products.filter(product => 
        targetCollection.productHandles!.includes(product.handle)
      );

      return collectionProducts;
    } catch (error: any) {
      productionLogger.error('Error fetching products by collection:', error);
      toast.error("Error", {
        description: "Failed to load collection products",
      });
      return [];
    }
  };

  /**
   * Create Shopify cart permalink
   */
  const createCartPermalink = (items: Array<{ variantId: string; quantity: number }>) => {
    return apiCreateCartPermalink(items);
  };

  /**
   * Search and filter products
   * Preserves EXACT logic from useShopify.searchProducts()
   */
  const searchProducts = (
    query: string, 
    collectionHandle?: string, 
    sortBy: 'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated' = 'updated'
  ) => {
    let filtered = [...products];

    // Filter by search query (exact word boundary matching)
    if (query.trim()) {
      const searchTerm = query.toLowerCase();
      filtered = filtered.filter(product => {
        // Create a regex pattern for exact word matching
        const wordBoundaryPattern = new RegExp(`\\b${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        
        return (
          wordBoundaryPattern.test(product.title) ||
          wordBoundaryPattern.test(product.description) ||
          product.tags.some(tag => wordBoundaryPattern.test(tag)) ||
          wordBoundaryPattern.test(product.productType)
        );
      });
    }

    // Filter by collection using Shopify's native collection data only
    if (collectionHandle) {
      const targetCollection = collections.find(collection => collection.handle === collectionHandle);
      
      if (targetCollection && targetCollection.productHandles && targetCollection.productHandles.length > 0) {
        // Use Shopify collection with actual product handles
        filtered = filtered.filter(product => 
          targetCollection.productHandles!.includes(product.handle)
        );
      } else {
        // Collection exists but has no products, or collection doesn't exist
        filtered = [];
      }
    }

    // Sort results
    switch (sortBy) {
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'price_asc':
        filtered.sort((a, b) => 
          parseFloat(a.priceRange.minVariantPrice.amount) - parseFloat(b.priceRange.minVariantPrice.amount)
        );
        break;
      case 'price_desc':
        filtered.sort((a, b) => 
          parseFloat(b.priceRange.maxVariantPrice.amount) - parseFloat(a.priceRange.maxVariantPrice.amount)
        );
        break;
      case 'updated':
        filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      default:
        break;
    }

    return filtered;
  };

  /**
   * Get featured products
   * Preserves EXACT logic from useShopify.getFeaturedProducts()
   */
  const getFeaturedProducts = () => {
    // Prefer Shopify-tagged "featured" products (case-insensitive). Fallback to first 4.
    const featured = products.filter(p =>
      (p.tags || []).some(t => t?.toLowerCase?.() === 'featured' || t?.toLowerCase?.().includes('featured'))
    );
    return (featured.length ? featured : products).slice(0, 4);
  };

  /**
   * Refetch all data
   */
  const refetch = () => {
    refetchProducts();
    refetchCollections();
  };

  /**
   * Clear cache and refetch (replaces clearCacheAndRefresh)
   */
  const clearCacheAndRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products });
    await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collections });
    refetch();
  };

  /**
   * Silent refresh (background refetch without loading state)
   */
  const silentRefresh = async () => {
    try {
      await refetchProducts();
      await refetchCollections();
    } catch (error) {
      productionLogger.error('Silent refresh error:', error);
    }
  };

  return {
    products,
    collections,
    loading,
    allProductsLoading,
    getProductByHandle,
    getProductsByCollection,
    searchProducts,
    getFeaturedProducts,
    createCartPermalink,
    refetch,
    clearCacheAndRefresh,
    silentRefresh,
  };
};

/**
 * Smart paginated collection hook with background loading
 * Shows first page instantly, loads all products in background for search
 */
export const usePaginatedCollection = (collectionHandle: string, page: number = 1, perPage: number = 24) => {
  const queryClient = useQueryClient();
  const { searchProducts, allProductsLoading } = useShopifyQuery();

  // Fetch first page instantly
  const {
    data: pageData,
    isLoading: pageLoading,
    error: pageError,
  } = useQuery({
    queryKey: QUERY_KEYS.collectionPage(collectionHandle, page),
    queryFn: () => fetchCollectionPage(collectionHandle, page, perPage),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Get all collection products from main cache (loads in background)
  const allCollectionProducts = searchProducts('', collectionHandle, 'updated');

  return {
    // Initial page data (instant)
    products: pageData?.products || [],
    hasNextPage: pageData?.hasNextPage || false,
    hasPreviousPage: pageData?.hasPreviousPage || false,
    currentPage: pageData?.currentPage || page,
    
    // Background loading state
    isInitialLoading: pageLoading,
    isBackgroundLoading: allProductsLoading,
    
    // All products (for search) - available once background load completes
    allProducts: allCollectionProducts,
    
    error: pageError,
  };
};
